import { useState } from 'react'
import {
  Palette,
  Type,
  Layout,
  Sparkles,
  Check,
  X,
  Copy,
  CheckCircle,
  Home,
  Users,
  FileText,
  Settings,
  Search,
  Plus,
  Edit3,
  Trash2,
  Download,
  Compass,
  Network,
  Target,
  Briefcase,
  Lightbulb,
  MessageSquare,
  AlertTriangle,
  Info,
  XCircle,
  Box,
  Layers,
  GitBranch
} from 'lucide-react'

export function DesignSystemSection() {
  const [copiedVar, setCopiedVar] = useState<string | null>(null)

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text)
    setCopiedVar(label)
    setTimeout(() => setCopiedVar(null), 2000)
  }

  return (
    <div className="space-y-12">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-market via-business to-solution
                          flex items-center justify-center shadow-md">
            <Palette className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-text">Design System</h1>
            <p className="text-sm text-text-secondary">Warm Minimal Visual Language v1.0</p>
          </div>
        </div>
        <p className="text-text-secondary">
          A cohesive design system built around the Market-Business-Solution ontology, featuring warm neutrals,
          domain-driven colors, and consistent typography. All components use CSS variables for maintainability.
        </p>
      </div>

      {/* Quick Reference */}
      <div className="bg-gradient-to-br from-warm-50 to-warm-100 rounded-xl p-6 border border-warm-200">
        <h3 className="font-semibold text-market mb-4 flex items-center gap-2">
          <Sparkles className="w-5 h-5" />
          Quick Reference
        </h3>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg p-4 border border-warm-200">
            <div className="text-xs text-text-muted uppercase tracking-wide mb-2">Source</div>
            <code className="text-sm text-market font-mono">apps/web/src/index.css</code>
          </div>
          <div className="bg-white rounded-lg p-4 border border-warm-200">
            <div className="text-xs text-text-muted uppercase tracking-wide mb-2">Documentation</div>
            <code className="text-sm text-business font-mono">docs/claude_guides/VISUAL_DESIGN.md</code>
          </div>
          <div className="bg-white rounded-lg p-4 border border-warm-200">
            <div className="text-xs text-text-muted uppercase tracking-wide mb-2">Visual Guide</div>
            <code className="text-sm text-solution font-mono">docs/design-system-visual-guide.html</code>
          </div>
        </div>
      </div>

      {/* Domain Colors */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <Layers className="w-5 h-5 text-market" />
          <h2 className="text-xl font-semibold text-text">Domain Colors</h2>
        </div>
        <p className="text-sm text-text-secondary mb-6">
          Three primary colors representing the Market-Business-Solution ontology layers. Each has light (hover) and dark (active) variants.
        </p>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Market */}
          <div className="group">
            <div className="bg-white rounded-xl border border-warm-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow">
              <div className="h-32 bg-market relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-market-light/20 to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Target className="w-5 h-5 text-white" />
                    <span className="font-semibold text-white text-lg">Market</span>
                  </div>
                  <p className="text-white/90 text-xs">Teal-Sage Green</p>
                </div>
              </div>
              <div className="p-4 space-y-3">
                <ColorVariable
                  name="--color-market"
                  value="#5c8a6b"
                  copied={copiedVar === 'market'}
                  onCopy={() => copyToClipboard('var(--color-market)', 'market')}
                />
                <ColorVariable
                  name="--color-market-light"
                  value="#7aab8a"
                  copied={copiedVar === 'market-light'}
                  onCopy={() => copyToClipboard('var(--color-market-light)', 'market-light')}
                  isVariant
                />
                <ColorVariable
                  name="--color-market-dark"
                  value="#456b52"
                  copied={copiedVar === 'market-dark'}
                  onCopy={() => copyToClipboard('var(--color-market-dark)', 'market-dark')}
                  isVariant
                />
              </div>
            </div>
            <div className="mt-3 text-xs text-text-muted px-1">
              Use for: Personas, segments, market analysis
            </div>
          </div>

          {/* Business */}
          <div className="group">
            <div className="bg-white rounded-xl border border-warm-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow">
              <div className="h-32 bg-business relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-business-light/20 to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Briefcase className="w-5 h-5 text-white" />
                    <span className="font-semibold text-white text-lg">Business</span>
                  </div>
                  <p className="text-white/90 text-xs">Teal-Blue</p>
                </div>
              </div>
              <div className="p-4 space-y-3">
                <ColorVariable
                  name="--color-business"
                  value="#3d7a8c"
                  copied={copiedVar === 'business'}
                  onCopy={() => copyToClipboard('var(--color-business)', 'business')}
                />
                <ColorVariable
                  name="--color-business-light"
                  value="#5a9aad"
                  copied={copiedVar === 'business-light'}
                  onCopy={() => copyToClipboard('var(--color-business-light)', 'business-light')}
                  isVariant
                />
                <ColorVariable
                  name="--color-business-dark"
                  value="#2d5a66"
                  copied={copiedVar === 'business-dark'}
                  onCopy={() => copyToClipboard('var(--color-business-dark)', 'business-dark')}
                  isVariant
                />
              </div>
            </div>
            <div className="mt-3 text-xs text-text-muted px-1">
              Use for: Strategies, revenue, channels
            </div>
          </div>

          {/* Solution */}
          <div className="group">
            <div className="bg-white rounded-xl border border-warm-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow">
              <div className="h-32 bg-solution relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-solution-light/20 to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Lightbulb className="w-5 h-5 text-text" />
                    <span className="font-semibold text-text text-lg">Solution</span>
                  </div>
                  <p className="text-text/80 text-xs">Warm Gold</p>
                </div>
              </div>
              <div className="p-4 space-y-3">
                <ColorVariable
                  name="--color-solution"
                  value="#c9a857"
                  copied={copiedVar === 'solution'}
                  onCopy={() => copyToClipboard('var(--color-solution)', 'solution')}
                />
                <ColorVariable
                  name="--color-solution-light"
                  value="#dbc580"
                  copied={copiedVar === 'solution-light'}
                  onCopy={() => copyToClipboard('var(--color-solution-light)', 'solution-light')}
                  isVariant
                />
                <ColorVariable
                  name="--color-solution-dark"
                  value="#9a7f3f"
                  copied={copiedVar === 'solution-dark'}
                  onCopy={() => copyToClipboard('var(--color-solution-dark)', 'solution-dark')}
                  isVariant
                />
              </div>
            </div>
            <div className="mt-3 text-xs text-text-muted px-1">
              Use for: Features, requirements, tech
            </div>
          </div>
        </div>
      </section>

      {/* Warm Neutral Palette */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <Box className="w-5 h-5 text-text-secondary" />
          <h2 className="text-xl font-semibold text-text">Warm Neutral Palette</h2>
        </div>
        <p className="text-sm text-text-secondary mb-6">
          10-step grayscale with warm undertones. Never use pure grays - always use these warm neutrals.
        </p>

        <div className="grid grid-cols-5 md:grid-cols-10 gap-3">
          {[
            { step: '50', hex: '#fdfcfa', usage: 'Page BG' },
            { step: '100', hex: '#f8f6f1', usage: 'Sidebar' },
            { step: '200', hex: '#f0ece3', usage: 'Subtle Border' },
            { step: '300', hex: '#e4ddd1', usage: 'Border' },
            { step: '400', hex: '#c9bfae', usage: 'Scrollbar' },
            { step: '500', hex: '#a69985', usage: 'Placeholder' },
            { step: '600', hex: '#847561', usage: 'Muted' },
            { step: '700', hex: '#635849', usage: 'Secondary' },
            { step: '800', hex: '#453d33', usage: 'Primary Btn' },
            { step: '900', hex: '#2a241e', usage: 'Text' },
          ].map((color) => (
            <button
              key={color.step}
              onClick={() => copyToClipboard(`var(--color-warm-${color.step})`, `warm-${color.step}`)}
              className="group relative rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-all"
            >
              <div
                className="h-20 w-full"
                style={{ backgroundColor: color.hex }}
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
              <div className="p-2 bg-white border-t border-warm-200">
                <div className="font-mono text-xs font-semibold text-text">{color.step}</div>
                <div className="font-mono text-[10px] text-text-muted">{color.hex}</div>
                <div className="text-[9px] text-text-muted mt-0.5">{color.usage}</div>
              </div>
              {copiedVar === `warm-${color.step}` && (
                <div className="absolute top-1 right-1 bg-market text-white rounded-md px-1.5 py-0.5 text-[10px] font-medium">
                  Copied!
                </div>
              )}
            </button>
          ))}
        </div>
      </section>

      {/* Typography */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <Type className="w-5 h-5 text-text-secondary" />
          <h2 className="text-xl font-semibold text-text">Typography</h2>
        </div>

        <div className="bg-white rounded-xl border border-warm-200 overflow-hidden">
          <div className="divide-y divide-warm-200">
            {/* Heading 1 */}
            <div className="p-6 hover:bg-warm-50/50 transition-colors">
              <div className="grid md:grid-cols-[200px_1fr] gap-4 items-baseline">
                <div>
                  <div className="text-sm font-semibold text-text mb-1">Heading 1</div>
                  <div className="text-xs text-text-muted">DM Sans Bold 32px</div>
                  <code className="text-[10px] text-market font-mono mt-1 block">text-2xl font-bold</code>
                </div>
                <div className="text-[32px] font-bold font-display leading-tight text-text">
                  Market Intelligence Platform
                </div>
              </div>
            </div>

            {/* Heading 2 */}
            <div className="p-6 hover:bg-warm-50/50 transition-colors">
              <div className="grid md:grid-cols-[200px_1fr] gap-4 items-baseline">
                <div>
                  <div className="text-sm font-semibold text-text mb-1">Heading 2</div>
                  <div className="text-xs text-text-muted">DM Sans Semibold 22px</div>
                  <code className="text-[10px] text-market font-mono mt-1 block">text-xl font-semibold</code>
                </div>
                <div className="text-[22px] font-semibold font-display leading-tight text-text">
                  Discovery Workflow
                </div>
              </div>
            </div>

            {/* Heading 3 */}
            <div className="p-6 hover:bg-warm-50/50 transition-colors">
              <div className="grid md:grid-cols-[200px_1fr] gap-4 items-baseline">
                <div>
                  <div className="text-sm font-semibold text-text mb-1">Heading 3</div>
                  <div className="text-xs text-text-muted">DM Sans Semibold 16px</div>
                  <code className="text-[10px] text-market font-mono mt-1 block">text-base font-semibold</code>
                </div>
                <div className="text-base font-semibold font-display text-text">
                  Business Model Canvas
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="p-6 hover:bg-warm-50/50 transition-colors">
              <div className="grid md:grid-cols-[200px_1fr] gap-4 items-baseline">
                <div>
                  <div className="text-sm font-semibold text-text mb-1">Body</div>
                  <div className="text-xs text-text-muted">Inter Regular 14px</div>
                  <code className="text-[10px] text-market font-mono mt-1 block">text-sm</code>
                </div>
                <div className="text-sm text-text leading-relaxed">
                  Semantic web-powered business intelligence platform combining RDF/SPARQL graph databases with traditional PostgreSQL storage.
                </div>
              </div>
            </div>

            {/* Body Small */}
            <div className="p-6 hover:bg-warm-50/50 transition-colors">
              <div className="grid md:grid-cols-[200px_1fr] gap-4 items-baseline">
                <div>
                  <div className="text-sm font-semibold text-text mb-1">Body Small</div>
                  <div className="text-xs text-text-muted">Inter Regular 13px</div>
                  <code className="text-[10px] text-market font-mono mt-1 block">text-xs text-text-secondary</code>
                </div>
                <div className="text-xs text-text-secondary leading-relaxed">
                  Extract entities and insights from documents with AI-powered analysis.
                </div>
              </div>
            </div>

            {/* Caption */}
            <div className="p-6 hover:bg-warm-50/50 transition-colors">
              <div className="grid md:grid-cols-[200px_1fr] gap-4 items-baseline">
                <div>
                  <div className="text-sm font-semibold text-text mb-1">Caption</div>
                  <div className="text-xs text-text-muted">Inter Regular 12px</div>
                  <code className="text-[10px] text-market font-mono mt-1 block">text-[10px] text-text-muted</code>
                </div>
                <div className="text-[12px] text-text-muted">
                  Created on December 31, 2025
                </div>
              </div>
            </div>

            {/* Monospace */}
            <div className="p-6 hover:bg-warm-50/50 transition-colors">
              <div className="grid md:grid-cols-[200px_1fr] gap-4 items-baseline">
                <div>
                  <div className="text-sm font-semibold text-text mb-1">Monospace</div>
                  <div className="text-xs text-text-muted">JetBrains Mono 13px</div>
                  <code className="text-[10px] text-market font-mono mt-1 block">font-mono text-xs</code>
                </div>
                <code className="font-mono text-xs text-text">
                  const value = "market:Persona";
                </code>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Icons */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-5 h-5 text-text-secondary" />
          <h2 className="text-xl font-semibold text-text">Icons</h2>
        </div>
        <p className="text-sm text-text-secondary mb-6">
          Use Lucide React icons exclusively. Flat outline style only - no filled, solid, or 3D variants.
        </p>

        <div className="bg-white rounded-xl border border-warm-200 p-6">
          <div className="grid grid-cols-6 md:grid-cols-10 gap-3">
            {[
              { Icon: Home, name: 'home' },
              { Icon: Users, name: 'users' },
              { Icon: FileText, name: 'file-text' },
              { Icon: Settings, name: 'settings' },
              { Icon: Search, name: 'search' },
              { Icon: Plus, name: 'plus' },
              { Icon: Check, name: 'check' },
              { Icon: X, name: 'x' },
              { Icon: Edit3, name: 'edit-3' },
              { Icon: Trash2, name: 'trash-2' },
              { Icon: Download, name: 'download' },
              { Icon: Compass, name: 'compass' },
              { Icon: Network, name: 'network' },
              { Icon: Target, name: 'target' },
              { Icon: Briefcase, name: 'briefcase' },
              { Icon: Lightbulb, name: 'lightbulb' },
              { Icon: MessageSquare, name: 'message-square' },
              { Icon: CheckCircle, name: 'check-circle' },
              { Icon: AlertTriangle, name: 'alert-triangle' },
              { Icon: GitBranch, name: 'git-branch' },
            ].map(({ Icon, name }) => (
              <button
                key={name}
                onClick={() => copyToClipboard(`<${name.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join('')} />`, name)}
                className="flex flex-col items-center gap-2 p-3 rounded-lg border border-warm-200
                         hover:border-market hover:bg-market/5 transition-all group"
              >
                <Icon className="w-5 h-5 text-text group-hover:text-market transition-colors" />
                <span className="text-[9px] font-mono text-text-muted">{name}</span>
                {copiedVar === name && (
                  <span className="absolute -top-1 -right-1 bg-market text-white rounded-md px-1.5 py-0.5 text-[9px]">
                    ✓
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Buttons & Badges */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <Layout className="w-5 h-5 text-text-secondary" />
          <h2 className="text-xl font-semibold text-text">Components</h2>
        </div>

        {/* Buttons */}
        <div className="bg-white rounded-xl border border-warm-200 p-6 mb-6">
          <h3 className="text-sm font-semibold text-text mb-4">Buttons</h3>

          <div className="space-y-6">
            {/* Primary */}
            <div>
              <div className="text-xs text-text-muted uppercase tracking-wide mb-3">Primary Actions</div>
              <div className="flex flex-wrap gap-3">
                <button className="px-5 py-2.5 bg-primary hover:bg-primary-hover text-white rounded-md text-sm font-medium transition-colors flex items-center gap-2">
                  <Plus className="w-4 h-4" />
                  Primary
                </button>
                <button className="px-5 py-2.5 bg-market hover:bg-market-dark text-white rounded-md text-sm font-medium transition-colors flex items-center gap-2">
                  <Target className="w-4 h-4" />
                  Market
                </button>
                <button className="px-5 py-2.5 bg-business hover:bg-business-dark text-white rounded-md text-sm font-medium transition-colors flex items-center gap-2">
                  <Briefcase className="w-4 h-4" />
                  Business
                </button>
                <button className="px-5 py-2.5 bg-solution hover:bg-solution-dark text-white rounded-md text-sm font-medium transition-colors flex items-center gap-2">
                  <Lightbulb className="w-4 h-4" />
                  Solution
                </button>
              </div>
            </div>

            {/* Secondary */}
            <div>
              <div className="text-xs text-text-muted uppercase tracking-wide mb-3">Secondary Actions</div>
              <div className="flex flex-wrap gap-3">
                <button className="px-5 py-2.5 bg-white border border-warm-300 hover:border-market hover:bg-market/5 text-text rounded-md text-sm font-medium transition-colors flex items-center gap-2">
                  <Settings className="w-4 h-4" />
                  Outline
                </button>
                <button className="px-5 py-2.5 bg-transparent hover:bg-warm-100 text-text-secondary hover:text-text rounded-md text-sm font-medium transition-colors flex items-center gap-2">
                  <X className="w-4 h-4" />
                  Ghost
                </button>
                <button className="px-3 py-1.5 bg-white border border-warm-300 hover:border-market hover:bg-market/5 text-text rounded-md text-xs font-medium transition-colors flex items-center gap-1.5">
                  <Edit3 className="w-3.5 h-3.5" />
                  Small
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Badges */}
        <div className="bg-white rounded-xl border border-warm-200 p-6">
          <h3 className="text-sm font-semibold text-text mb-4">Badges</h3>
          <div className="flex flex-wrap gap-3">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-market text-white text-xs font-medium">
              <Check className="w-3 h-3" />
              Market
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-business text-white text-xs font-medium">
              <Briefcase className="w-3 h-3" />
              Business
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-solution text-text text-xs font-medium">
              <Lightbulb className="w-3 h-3" />
              Solution
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-warm-200 text-text-secondary text-xs font-medium">
              Draft
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-success/10 text-success border border-success/20 text-xs font-medium">
              <CheckCircle className="w-3 h-3" />
              Success
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-warning/10 text-warning border border-warning/20 text-xs font-medium">
              <AlertTriangle className="w-3 h-3" />
              Warning
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-error/10 text-error border border-error/20 text-xs font-medium">
              <XCircle className="w-3 h-3" />
              Error
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-info/10 text-info border border-info/20 text-xs font-medium">
              <Info className="w-3 h-3" />
              Info
            </span>
          </div>
        </div>
      </section>

      {/* Shadows & Spacing */}
      <section>
        <div className="grid md:grid-cols-2 gap-6">
          {/* Shadows */}
          <div className="bg-white rounded-xl border border-warm-200 p-6">
            <h3 className="text-sm font-semibold text-text mb-4">Shadows</h3>
            <p className="text-xs text-text-muted mb-4">
              Warm-tinted shadows using rgba(42, 36, 30, alpha)
            </p>
            <div className="space-y-6">
              <div className="text-center">
                <div className="w-24 h-24 mx-auto bg-white rounded-lg shadow-sm border border-warm-200 mb-2" />
                <div className="text-xs font-mono text-text-muted">shadow-sm</div>
                <code className="text-[10px] text-market">0 1px 2px (0.04)</code>
              </div>
              <div className="text-center">
                <div className="w-24 h-24 mx-auto bg-white rounded-lg shadow-md mb-2" />
                <div className="text-xs font-mono text-text-muted">shadow-md</div>
                <code className="text-[10px] text-market">0 4px 12px (0.06)</code>
              </div>
              <div className="text-center">
                <div className="w-24 h-24 mx-auto bg-white rounded-lg shadow-lg mb-2" />
                <div className="text-xs font-mono text-text-muted">shadow-lg</div>
                <code className="text-[10px] text-market">0 8px 24px (0.08)</code>
              </div>
            </div>
          </div>

          {/* Spacing */}
          <div className="bg-white rounded-xl border border-warm-200 p-6">
            <h3 className="text-sm font-semibold text-text mb-4">Spacing Scale</h3>
            <p className="text-xs text-text-muted mb-4">
              Consistent spacing creates visual rhythm
            </p>
            <div className="space-y-3">
              {[
                { name: 'xs', value: '4px', tailwind: 'gap-1' },
                { name: 'sm', value: '8px', tailwind: 'gap-2' },
                { name: 'md', value: '16px', tailwind: 'gap-4' },
                { name: 'lg', value: '24px', tailwind: 'gap-6' },
                { name: 'xl', value: '32px', tailwind: 'gap-8' },
                { name: '2xl', value: '48px', tailwind: 'gap-12' },
              ].map((space) => (
                <div key={space.name} className="flex items-center gap-4">
                  <div className="w-24 text-xs font-mono text-text-muted">
                    --spacing-{space.name}
                  </div>
                  <div className="flex-1">
                    <div
                      className="h-6 bg-market rounded"
                      style={{ width: space.value }}
                    />
                  </div>
                  <div className="w-16 text-xs text-text-muted text-right">
                    {space.value}
                  </div>
                  <code className="w-16 text-[10px] text-market text-right">
                    {space.tailwind}
                  </code>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Design Guidelines */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <CheckCircle className="w-5 h-5 text-success" />
          <h2 className="text-xl font-semibold text-text">Design Guidelines</h2>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Do's */}
          <div className="bg-white rounded-xl border border-success/20 overflow-hidden">
            <div className="bg-success/10 px-5 py-3 border-b border-success/20">
              <h3 className="font-semibold text-success text-sm flex items-center gap-2">
                <Check className="w-4 h-4" />
                Do
              </h3>
            </div>
            <div className="p-5">
              <ul className="space-y-2 text-sm text-text-secondary">
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-success flex-shrink-0 mt-0.5" />
                  <span>Use Lucide React icons exclusively</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-success flex-shrink-0 mt-0.5" />
                  <span>Use flat/outline icon style only</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-success flex-shrink-0 mt-0.5" />
                  <span>Apply domain colors for Market/Business/Solution</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-success flex-shrink-0 mt-0.5" />
                  <span>Use warm-100 for sidebar and elevated surfaces</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-success flex-shrink-0 mt-0.5" />
                  <span>Use subtle shadows with warm tint</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-success flex-shrink-0 mt-0.5" />
                  <span>Keep border radius consistent (6-20px)</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-success flex-shrink-0 mt-0.5" />
                  <span>Use Inter for body, DM Sans for headings</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-success flex-shrink-0 mt-0.5" />
                  <span>Use CSS variables, never hardcode colors</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Don'ts */}
          <div className="bg-white rounded-xl border border-error/20 overflow-hidden">
            <div className="bg-error/10 px-5 py-3 border-b border-error/20">
              <h3 className="font-semibold text-error text-sm flex items-center gap-2">
                <X className="w-4 h-4" />
                Don't
              </h3>
            </div>
            <div className="p-5">
              <ul className="space-y-2 text-sm text-text-secondary">
                <li className="flex items-start gap-2">
                  <X className="w-4 h-4 text-error flex-shrink-0 mt-0.5" />
                  <span>Don't use other icon libraries (Font Awesome, etc.)</span>
                </li>
                <li className="flex items-start gap-2">
                  <X className="w-4 h-4 text-error flex-shrink-0 mt-0.5" />
                  <span>Don't use filled/solid icon variants</span>
                </li>
                <li className="flex items-start gap-2">
                  <X className="w-4 h-4 text-error flex-shrink-0 mt-0.5" />
                  <span>Don't use pure gray (#888, #ccc)</span>
                </li>
                <li className="flex items-start gap-2">
                  <X className="w-4 h-4 text-error flex-shrink-0 mt-0.5" />
                  <span>Don't use harsh black shadows rgba(0,0,0,x)</span>
                </li>
                <li className="flex items-start gap-2">
                  <X className="w-4 h-4 text-error flex-shrink-0 mt-0.5" />
                  <span>Don't mix domain colors arbitrarily</span>
                </li>
                <li className="flex items-start gap-2">
                  <X className="w-4 h-4 text-error flex-shrink-0 mt-0.5" />
                  <span>Don't use 3D effects or icon gradients</span>
                </li>
                <li className="flex items-start gap-2">
                  <X className="w-4 h-4 text-error flex-shrink-0 mt-0.5" />
                  <span>Don't use sharp corners (0px radius)</span>
                </li>
                <li className="flex items-start gap-2">
                  <X className="w-4 h-4 text-error flex-shrink-0 mt-0.5" />
                  <span>Don't use emojis as icons</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

interface ColorVariableProps {
  name: string
  value: string
  copied: boolean
  onCopy: () => void
  isVariant?: boolean
}

function ColorVariable({ name, value, copied, onCopy, isVariant }: ColorVariableProps) {
  return (
    <button
      onClick={onCopy}
      className="flex items-center justify-between p-2 rounded-lg hover:bg-warm-50 transition-colors group w-full"
    >
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <div
          className="w-6 h-6 rounded border border-warm-300 flex-shrink-0"
          style={{ backgroundColor: value }}
        />
        <div className="flex flex-col items-start min-w-0 flex-1">
          <code className={`font-mono text-xs ${isVariant ? 'text-text-muted' : 'text-text font-medium'} truncate w-full text-left`}>
            {name}
          </code>
          <span className="font-mono text-[10px] text-text-muted">{value}</span>
        </div>
      </div>
      {copied ? (
        <Check className="w-4 h-4 text-success flex-shrink-0" />
      ) : (
        <Copy className="w-4 h-4 text-text-muted opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
      )}
    </button>
  )
}
